"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { countWords } from "@/lib/tool-kit/admissions/counts";
import {
  buildResearchStatement,
  MAX_WORDS,
  MIN_WORDS,
  PAGE_CHOICES,
  type PageChoice,
  RESEARCH_INSTITUTIONS,
  type ResearchInstitution,
  type ResearchStatementInput,
  researchWordTotal,
} from "@/lib/tool-kit/admissions/research-statement";

/** English strings. A translated content file overrides these through `ui`. */
export const RESEARCH_STATEMENT_OUTLINE_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  aboutHeading: "Your application",
  institutionLabel: "Type of institution",
  institutions: {
    research: "Research university",
    teaching: "Teaching-focused institution",
  },
  institutionHint:
    "Changes the word split: a research university reads for a fundable programme, a teaching-focused one for research undergraduates can join.",
  nameLabel: "Your name (for the title)",
  namePlaceholder: "Adaeze Okafor",
  lengthLabel: "Length",
  lengthOptions: {
    pages2: "Two pages (about 1,000 words)",
    pages3: "Three pages (about 1,500)",
    pages4: "Four pages (about 2,000)",
    custom: "Another length",
  },
  lengthHint:
    "Typical lengths: one to two pages in the humanities, two to three in the social sciences, three to four in the laboratory sciences. The job advertisement's limit always wins.",
  customLabel: "Target (words)",
  customHint: "Between {min} and {max}.",
  overviewHeading: "Overview",
  overviewLabel: "Your research theme in two or three sentences",
  overviewPlaceholder:
    "I study how coral reefs recover from bleaching, and what local conditions decide whether they do.",
  pastHeading: "Past work",
  pastLabel: "Completed projects — one per line, “Project — what you found”",
  pastPlaceholder:
    "Doctoral study of 18 reef sites — water quality predicted recovery better than temperature history",
  currentHeading: "Current work",
  currentLabel: "Current projects — one per line, “Project — where it stands”",
  currentPlaceholder:
    "Four-year monitoring dataset — three papers out, a fourth in preparation",
  futureHeading: "Future work",
  futureLabel: "Planned projects — one per line, “Project — the first step”",
  futurePlaceholder:
    "Steering recovery by cutting local runoff — a pilot with two fishing cooperatives",
  impactHeading: "Impact",
  impactLabel: "Why it matters, and to whom",
  impactPlaceholder:
    "Who will use the results: researchers, managers, communities.",
  fundingHeading: "Funding plans",
  fundingLabel:
    "Funders you would approach — one per line, “Funder or scheme — what it would support”",
  fundingPlaceholder:
    "A national early-career award — the pilot and first doctoral student\nA conservation foundation — fieldwork with the cooperatives",
  peopleHeading: "People",
  peopleLabel: "Collaborators, students and partners — one per line",
  peoplePlaceholder:
    "Dr. Mei Tan — monitoring methods\nUndergraduate researchers in the field course",
  checkHeading: "Check your finished statement’s length",
  checkLabel: "Paste your finished statement (optional)",
  checkHint: "Counted in your browser; nothing is uploaded.",
  wordCount: "one:{count} word|other:{count} words",
  checkOver: "{count} over your target of {target}.",
  checkUnder: "{count} under your target of {target}.",
  checkOn: "Exactly on your target of {target}.",
  checkNoTarget: "Set a target length above to compare.",
  fileName: "research-statement-outline",
  document: {
    heading: "Your research statement outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every section has your content.",
  },
  defaultTitle: "Research Statement",
  subtitles: {
    research: "Research statement outline · research university",
    teaching: "Research statement outline · teaching-focused institution",
  },
  subtitleWords: "{kind} · {words} words",
  budgetHeading: "Word Budget",
  budgetNote:
    "A typical split, not a rule. If the advertisement names sections or a limit, follow it and use this as a pacing guide.",
  budget: {
    section: "Section",
    share: "Share",
    words: "Target words",
    total: "Total",
  },
  sections: {
    overview: "1. Overview",
    past: "2. Past Work",
    current: "3. Current Work",
    future: "4. Future Work",
    impact: "5. Impact",
    funding: "6. Funding Plans",
    people: "7. People and Collaboration",
  },
  target: "Target: about {words} words ({share}).",
  targetShare: "Target: about {share} of the statement.",
  tips: {
    research: {
      overview:
        "State your theme and the big question in plain words a reader outside your subfield can follow.",
      past: "Lead with what you found, not what you did. Two or three results, each with its significance.",
      current:
        "Show momentum: what is in progress, what is submitted, and how it grows from the past work.",
      future:
        "The heart of the statement. Two or three projects, each with a question, a first step, and why you are the person to do it.",
      impact:
        "Say who will use the results and what changes. Be specific, and honest about what is a hope.",
      funding:
        "Name real funders and schemes that fit, and which project each would support. Committees read this as evidence the programme is fundable.",
      people:
        "Who you will work with and which students you will train. Name real colleagues only if they are at the institution or are genuine collaborators.",
    },
    teaching: {
      overview:
        "State your theme in plain words. A committee from another subfield should follow it.",
      past: "Lead with what you found. Two or three results with their significance.",
      current:
        "Show momentum and a project that can run alongside a full teaching load.",
      future:
        "Projects that are realistic with a teaching load and that undergraduates can join, each with a clear first step.",
      impact:
        "Who benefits, including the students who take part. Be honest about what is a hope.",
      funding:
        "Funders and schemes that support undergraduate-involved research or small-scale projects, and which project each would support.",
      people:
        "How undergraduates join your work, what they do, and what they gain. Name collaborators who really exist.",
    },
  },
  prompts: {
    overview:
      "Describe your research theme and the central question in two or three sentences.",
    past: "List the projects you have completed and what each found.",
    current: "List what you are working on now and where each project stands.",
    future:
      "Describe two or three projects you would start, each with its question and a first step.",
    impact: "Say who will use the results and what they will change.",
    funding:
      "Name the funders and schemes you would approach, and which project each would support.",
    people:
      "Name collaborators, the students you would train, and any partners.",
  },
  futureHead: { project: "Project", step: "First step" },
  fundingHead: {
    funder: "Funder or scheme",
    supports: "What it would support",
  },
  checklistHeading: "Before You Submit",
  checklist: [
    "I followed the advertisement’s length limit and any required headings.",
    "A reader outside my subfield can follow the first paragraph.",
    "The future work is specific enough that a reader can see the first year.",
    "Every funder and colleague I named is real and appropriate.",
    "I did not claim results I do not have.",
    "Someone outside my group read it and told me what they thought it said.",
  ],
  example: {
    name: "Adaeze Okafor",
    overview:
      "I study how coral reefs recover from bleaching, and what local conditions decide whether they do.",
    past: "Doctoral study of 18 reef sites — water quality predicted recovery better than temperature history\nA reef-survey method — now used by two regional monitoring teams",
    current:
      "Four-year monitoring dataset — three papers out, a fourth in preparation",
    future:
      "Steering recovery by cutting local runoff — a pilot with two fishing cooperatives\nA reef recovery model that uses community monitoring data — a first version built from the existing dataset",
    impact:
      "Reef managers in the Gulf of Guinea currently set priorities without recovery data. The pilot would show whether cutting runoff speeds recovery, and the model would let managers test options before they act.",
    funding:
      "A national early-career award — the pilot and the first doctoral student\nA conservation foundation — fieldwork with the cooperatives",
    people:
      "Dr. Mei Tan — monitoring methods\nUndergraduate researchers in the field course\nTwo fishing cooperatives that host the surveys",
  },
} as const;

const EMPTY: ResearchStatementInput = {
  institution: "research",
  name: "",
  words: "",
  overview: "",
  past: "",
  current: "",
  future: "",
  impact: "",
  funding: "",
  people: "",
};

type TextKey = Exclude<keyof ResearchStatementInput, "institution">;

const FIELDS: readonly {
  key: Exclude<TextKey, "name" | "words">;
  heading: keyof typeof RESEARCH_STATEMENT_OUTLINE_STRINGS & `${string}Heading`;
  rows: number;
}[] = [
  { key: "overview", heading: "overviewHeading", rows: 3 },
  { key: "past", heading: "pastHeading", rows: 3 },
  { key: "current", heading: "currentHeading", rows: 3 },
  { key: "future", heading: "futureHeading", rows: 4 },
  { key: "impact", heading: "impactHeading", rows: 3 },
  { key: "funding", heading: "fundingHeading", rows: 3 },
  { key: "people", heading: "peopleHeading", rows: 3 },
];

export default function ResearchStatementOutlineWidget() {
  const strings = useToolUi(RESEARCH_STATEMENT_OUTLINE_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<ResearchStatementInput>(EMPTY);
  const [choice, setChoice] = useState<PageChoice>("pages3");
  const [finished, setFinished] = useState("");
  const doc = useMemo(
    () => buildResearchStatement(input, strings, choice, fmt),
    [input, strings, choice, fmt]
  );
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const total = researchWordTotal(choice, input.words);
  const written = countWords(finished);
  const words = (count: number) =>
    tPlural(strings.wordCount, locale, count, { count: fmt(count) });

  let comparison: string = strings.checkNoTarget;
  if (total !== null) {
    const gap = written - total;
    const values = { count: words(Math.abs(gap)), target: fmt(total) };
    if (gap > 0) {
      comparison = t(strings.checkOver, values);
    } else if (gap < 0) {
      comparison = t(strings.checkUnder, values);
    } else {
      comparison = t(strings.checkOn, values);
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput({ ...EMPTY, ...strings.example })}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setInput(EMPTY);
            setFinished("");
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.aboutHeading}>
        <PillGroup
          label={strings.institutionLabel}
          name="rso-institution"
          onChange={(institution: ResearchInstitution) =>
            setInput((previous) => ({ ...previous, institution }))
          }
          options={RESEARCH_INSTITUTIONS.map((value) => ({
            value,
            label: strings.institutions[value],
          }))}
          value={input.institution}
        />
        <p className={HINT}>{strings.institutionHint}</p>
        <PillGroup
          label={strings.lengthLabel}
          name="rso-length"
          onChange={setChoice}
          options={PAGE_CHOICES.map((value) => ({
            value,
            label: strings.lengthOptions[value],
          }))}
          value={choice}
        />
        <p className={HINT}>{strings.lengthHint}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="rso-name"
            label={strings.nameLabel}
            onChange={update("name")}
            placeholder={strings.namePlaceholder}
            value={input.name}
          />
          {choice === "custom" ? (
            <TextField
              hint={t(strings.customHint, {
                min: fmt(MIN_WORDS),
                max: fmt(MAX_WORDS),
              })}
              id="rso-words"
              label={strings.customLabel}
              numeric
              onChange={update("words")}
              value={input.words}
            />
          ) : null}
        </div>
      </Panel>

      {FIELDS.map(({ key, heading, rows }) => (
        <Panel key={key} title={strings[heading]}>
          <TextAreaField
            id={`rso-${key}`}
            label={strings[`${key}Label`]}
            onChange={update(key)}
            placeholder={strings[`${key}Placeholder`]}
            prose
            rows={rows}
            value={input[key]}
          />
        </Panel>
      ))}

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="rso"
        labels={strings.document}
      />

      <Panel title={strings.checkHeading}>
        <TextAreaField
          hint={strings.checkHint}
          id="rso-finished"
          label={strings.checkLabel}
          onChange={setFinished}
          prose
          rows={6}
          value={finished}
        />
        <p aria-live="polite" className={HINT}>
          {words(written)}
          {finished.trim() === "" ? "" : ` — ${comparison}`}
        </p>
      </Panel>
    </div>
  );
}
